import type { EChartsOption } from "echarts-for-react"
import ReactECharts from "echarts-for-react/lib/core"
import { LineChart } from "echarts/charts"
import * as echarts from "echarts/core"
import { SVGRenderer } from "echarts/renderers"
import merge from "lodash/merge"
import {
  ComponentProps,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react"

import { cn, getCssVar, getThemeColor } from "@/lib/utils"
import useResizeObserver, {
  UseResizeObserverCallback,
} from "@react-hook/resize-observer"
import {
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconChartBar,
} from "@tabler/icons-react"

import { Button } from "./Button"
import { EmptyState } from "./EmptyState"
import { Fullscreen } from "./Fullscreen"

echarts.use([SVGRenderer, LineChart])

export interface ChartProps {
  icon?: ReactNode
  title?: string
  actions?: ReactNode
  /** Options object passed to ECharts, merged with some default options. Memoizing this is recommended. */
  options: EChartsOption
  className?: string

  isEmpty?: boolean
  tooltipHideEmptyValues?: boolean
  showFullScreen?: boolean
  onChartReady?: ComponentProps<typeof ReactECharts>["onChartReady"]
}

export function Chart({
  title,
  options,
  icon,
  actions,
  className,
  isEmpty = false,
  tooltipHideEmptyValues = false,
  showFullScreen = false,
  onChartReady,
}: ChartProps) {
  const [fullScreen, setFullScreen] = useState(false)
  const [chartContainerWidth, setChartContainerWidth] = useState<number | null>(null)
  const chartContainerRef = useRef<HTMLDivElement>(null)

  const resizeCallback = useCallback<UseResizeObserverCallback>(
    (entry) => setChartContainerWidth(entry.target.clientWidth),
    [],
  )
  useResizeObserver(chartContainerRef, resizeCallback)

  const eChartsOptions = useMemo(
    () =>
      merge(
        {
          textStyle: {
            fontFamily: getCssVar("font-sans"),
            color: getThemeColor("grey-500"),
          },
          axisPointer: {
            label: {
              backgroundColor: getThemeColor("grey-700"),
            },
          },
          legend: {
            icon: "circle",
            itemHeight: 12,
            bottom: 0,
            textStyle: {
              color: getThemeColor("grey-500"),
              fontSize: 16,
              fontWeight: 500,
            },
          },
          grid: {
            left: 100,
            right: 10,
            top: 30,
            bottom: 60,
          },
          tooltip: {
            appendToBody: true,
            formatter: (
              data: {
                axisValueLabel: string
                value: string | number
                marker: string
                seriesId: string
                seriesName: string
              }[],
            ) => {
              if (!options.tooltip) return

              // Building the tooltip content as a string avoids having to use dangerouslySetInnerHTML for the 'marker' tag
              let tooltipContent = `<div class="flex flex-col gap-0.5"><span>${data[0].axisValueLabel}</span>`
              data.map((item) => {
                if (!tooltipHideEmptyValues || !!item.value) {
                  tooltipContent += `<div key={item.seriesId} class="flex gap-4 justify-between">
                    <div>
                      <span>${item.marker}</span> 
                      <span>${item.seriesName}</span> 
                    </div>
                    <span class="font-medium text-colors-text-primary">${
                      options?.tooltip?.valueFormatter?.(item.value) ?? item.value
                    }</span>
                  </div>`
                }
              })
              tooltipContent += "</div>"
              return tooltipContent
            },
          },
          xAxis: {
            axisLine: false,
            axisLabel: {
              color: getThemeColor("grey-500"),
              fontSize: 14,
              fontWeight: 400,
            },
          },
          yAxis: {
            axisLine: {
              show: false,
              lineStyle: { color: getThemeColor("grey-500") },
            },
            axisLabel: {
              color: getThemeColor("grey-500"),
              fontSize: 14,
              fontWeight: 400,
            },
          },
        },
        options,
      ),
    [options, tooltipHideEmptyValues],
  )

  return (
    <div
      className={cn(
        "grid h-full w-full gap-3",
        title || icon || actions ? "grid-rows-[auto_minmax(0,1fr)]" : "grid-rows-1",
        className,
      )}
    >
      {title || icon || actions ? (
        <div className="flex gap-4">
          <div className="flex flex-grow items-center gap-3 text-h4 font-medium">
            {icon}
            {title}
          </div>
          {actions}
        </div>
      ) : null}
      <div ref={chartContainerRef} className="relative h-full w-full">
        {eChartsOptions && chartContainerWidth && !isEmpty ? (
          <Fullscreen enabled={fullScreen}>
            <ReactECharts
              onChartReady={onChartReady}
              echarts={echarts}
              option={eChartsOptions}
              style={{
                height: "100%",
                width: fullScreen ? "100%" : chartContainerWidth,
                position: "absolute",
              }}
              notMerge
            />
            {showFullScreen && (
              <Button
                onClick={() => setFullScreen(!fullScreen)}
                variant="secondary"
                className="absolute top-4 right-4"
                icon={fullScreen ? IconArrowsMinimize : IconArrowsMaximize}
                iconOnly
              />
            )}
          </Fullscreen>
        ) : null}
        {isEmpty ? (
          <EmptyState
            icon={IconChartBar}
            title="No data"
            className="h-full border-none bg-transparent"
          />
        ) : null}
      </div>
    </div>
  )
}
